// "Why we matched you" share-card generator.
//
// Renders a 1080×1920 (Instagram Story) PNG with:
//   ── two face circles up top (viewer + match)
//   ── headline (Twin Energy / Plot Twist)
//   ── 7 trait bars (eyes, nose, mouth, jaw, cheek_chin, proportions, symmetry)
//   ── DoppelCrush brand footer
//
// All drawing is client-side via Canvas so we don't burn a backend roundtrip.
// Image loading uses crossOrigin="anonymous" — the backend serves photos with
// CORS=*, so toBlob() succeeds without taint errors.

const W = 1080;
const H = 1920;

const TRAIT_LABELS = {
  eyes: "Eye spacing",
  nose: "Nose",
  mouth: "Mouth & lips",
  jaw: "Jawline",
  cheek_chin: "Cheekbones",
  proportions: "Face shape",
  symmetry: "Symmetry",
};

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawStrokedText(ctx, text, x, y, {
  font = "900 120px 'Bricolage Grotesque', 'Inter', system-ui, sans-serif",
  fill = "#ffffff",
  stroke = "#0f172a",
  strokeWidth = 14,
  align = "center",
  baseline = "alphabetic",
  rotate = 0,
} = {}) {
  ctx.save();
  ctx.translate(x, y);
  if (rotate) ctx.rotate(rotate);
  ctx.font = font;
  ctx.textAlign = align;
  ctx.textBaseline = baseline;
  ctx.lineJoin = "round";
  ctx.miterLimit = 2;
  ctx.lineWidth = strokeWidth;
  ctx.strokeStyle = stroke;
  ctx.strokeText(text, 0, 0);
  ctx.fillStyle = fill;
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null); // fall back to placeholder
    img.src = src;
  });
}

function drawCircleImage(ctx, img, cx, cy, radius, borderColor = "#0f172a") {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  if (img) {
    // cover-fit
    const s = Math.min(img.width, img.height);
    const sx = (img.width - s) / 2;
    const sy = (img.height - s) / 2;
    ctx.drawImage(img, sx, sy, s, s, cx - radius, cy - radius, radius * 2, radius * 2);
  } else {
    const grad = ctx.createLinearGradient(cx - radius, cy - radius, cx + radius, cy + radius);
    grad.addColorStop(0, "#ff2d8a");
    grad.addColorStop(1, "#ffd84d");
    ctx.fillStyle = grad;
    ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
  }
  ctx.restore();
  // border ring
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.lineWidth = 14;
  ctx.strokeStyle = borderColor;
  ctx.stroke();
}

/**
 * Mystery treatment for a face the matched user hasn't opted-in to publicly
 * sharing. We render a blush gradient circle with a sticker-style "?" so the
 * card stays on-brand and reads as a teaser instead of a missing photo.
 *
 * @param {string} initial — single uppercase letter (or "?") drawn as a subtle
 *   second-tier label under the question mark. Adds a hint of personalisation
 *   without leaking the person's identity.
 */
function drawMysteryCircle(ctx, cx, cy, radius, { initial = "?" } = {}) {
  // Filled blush gradient
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  const grad = ctx.createRadialGradient(cx, cy - radius * 0.3, radius * 0.1, cx, cy, radius);
  grad.addColorStop(0, "#ffe0ed");
  grad.addColorStop(0.6, "#ffb4d8");
  grad.addColorStop(1, "#ff7eb6");
  ctx.fillStyle = grad;
  ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
  // soft halftone for sticker feel
  ctx.fillStyle = "rgba(255,255,255,0.25)";
  for (let y = -radius; y < radius; y += 18) {
    for (let x = -radius; x < radius; x += 18) {
      ctx.beginPath();
      ctx.arc(cx + x, cy + y, 1.3, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
  // Glossy border ring
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.lineWidth = 14;
  ctx.strokeStyle = "#0f172a";
  ctx.stroke();
  // Big "?"
  ctx.save();
  ctx.font = "900 200px 'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineWidth = 14;
  ctx.lineJoin = "round";
  ctx.strokeStyle = "#0f172a";
  ctx.strokeText("?", cx, cy - 6);
  ctx.fillStyle = "#ffd84d";
  ctx.fillText("?", cx, cy - 6);
  ctx.restore();
  // Tiny initial chip below the ?
  if (initial && initial !== "?") {
    ctx.save();
    const chipW = 70, chipH = 30;
    roundRect(ctx, cx - chipW / 2, cy + radius - chipH - 6, chipW, chipH, chipH / 2);
    ctx.fillStyle = "#0f172a";
    ctx.fill();
    ctx.font = "800 18px 'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`${initial}???`, cx, cy + radius - chipH / 2 - 6);
    ctx.restore();
  }
}

/**
 * @param {Object} args
 * @param {string} args.viewerPhoto    — absolute URL to viewer selfie
 * @param {string} args.matchPhoto     — absolute URL to match selfie
 * @param {string} args.matchName      — match display name
 * @param {Object} args.breakdown      — backend breakdown {embedding_sim, geometry_sim, traits}
 * @param {"doppel"|"chaos"} args.mode — active mode for headline phrasing
 * @param {string} args.url            — share footer URL (cleaned)
 * @param {boolean} args.matchOptIn    — true ONLY if the matched user has
 *   toggled `share_opt_in` on their profile. When false (default), the
 *   match's face is rendered as a "?" silhouette and their name is hidden
 *   to keep their identity private. Privacy-first sharing.
 */
export async function generateBreakdownCard({
  viewerPhoto = "",
  matchPhoto = "",
  matchName = "",
  breakdown = null,
  mode = "doppel",
  url = "",
  matchOptIn = false,
} = {}) {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch { /* fonts api missing */ }
  }
  const [viewerImg, matchImg] = await Promise.all([
    loadImage(viewerPhoto),
    // Only load the match photo when they've opted in — keeps the request
    // network-clean and removes any chance of leaking the URL into the canvas.
    matchOptIn ? loadImage(matchPhoto) : Promise.resolve(null),
  ]);

  // --- Background gradient (mode-aware) ---
  const grad = ctx.createLinearGradient(0, 0, W, H);
  if (mode === "chaos") {
    grad.addColorStop(0, "#ffd84d");
    grad.addColorStop(0.5, "#ff6a3d");
    grad.addColorStop(1, "#8a5cf6");
  } else {
    grad.addColorStop(0, "#ffd1dc");
    grad.addColorStop(0.5, "#ff3d8a");
    grad.addColorStop(1, "#ff6a3d");
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // halftone dots
  ctx.fillStyle = "rgba(15,23,42,0.08)";
  for (let y = 0; y < H; y += 32) {
    for (let x = 0; x < W; x += 32) {
      ctx.beginPath();
      ctx.arc(x, y, 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // outer frame
  ctx.lineWidth = 14;
  ctx.strokeStyle = "#0f172a";
  roundRect(ctx, 36, 36, W - 72, H - 72, 56);
  ctx.stroke();

  // --- Top tag pill ---
  ctx.save();
  ctx.translate(W / 2, 160);
  ctx.rotate(-0.03);
  roundRect(ctx, -260, -42, 520, 84, 42);
  ctx.fillStyle = "#0f172a";
  ctx.fill();
  ctx.font = "800 36px 'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
  ctx.fillStyle = "#ffd84d";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("✦  DOPPELCRUSH  ✦", 0, 2);
  ctx.restore();

  // --- Two faces side by side ---
  const faceRadius = 170;
  const faceY = 470;
  drawCircleImage(ctx, viewerImg, W / 2 - 200, faceY, faceRadius, "#0f172a");
  // Mystery-by-default for the match face. Sharer can always show their own,
  // but the matched user must opt in via Profile to be revealed publicly.
  if (matchOptIn && matchImg) {
    drawCircleImage(ctx, matchImg, W / 2 + 200, faceY, faceRadius, "#0f172a");
  } else {
    drawMysteryCircle(ctx, W / 2 + 200, faceY, faceRadius, {
      initial: (matchName || "?").trim().slice(0, 1).toUpperCase() || "?",
    });
  }

  // ❤ heart between them
  ctx.save();
  ctx.translate(W / 2, faceY);
  ctx.fillStyle = "#ffffff";
  ctx.strokeStyle = "#0f172a";
  ctx.lineWidth = 10;
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(0, 18);
  ctx.bezierCurveTo(-50, -25, -90, 25, 0, 75);
  ctx.bezierCurveTo(90, 25, 50, -25, 0, 18);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();

  // --- Score callout ---
  const geomPct = breakdown?.geometry_sim != null
    ? Math.round((mode === "chaos" ? 1 - breakdown.geometry_sim : breakdown.geometry_sim) * 100)
    : (breakdown?.embedding_sim != null
        ? Math.round(Math.max(0, Math.min(1, breakdown.embedding_sim)) * 100)
        : 0);
  const calloutText = mode === "chaos" ? `${geomPct}% CONTRAST` : `${geomPct}% BONE-STRUCTURE MATCH`;
  drawStrokedText(ctx, calloutText, W / 2, 760, {
    font: "900 64px 'Bricolage Grotesque', 'Inter', system-ui, sans-serif",
    fill: "#ffffff",
    strokeWidth: 12,
    rotate: -0.02,
  });

  drawStrokedText(ctx, mode === "chaos" ? "PLOT TWIST" : "ANATOMICAL TWIN",
    W / 2, 870, {
      font: "900 88px 'Bricolage Grotesque', 'Inter', system-ui, sans-serif",
      fill: "#ffd84d",
      strokeWidth: 14,
      rotate: 0.015,
    });

  // --- Trait bars ---
  const traits = breakdown?.traits || null;
  const barsTop = 970;
  const rowH = 96;
  const barInsetX = 110;
  const barW = W - barInsetX * 2;

  if (traits) {
    const entries = Object.entries(traits)
      .filter(([k]) => TRAIT_LABELS[k])
      .sort((a, b) => (mode === "chaos" ? a[1] - b[1] : b[1] - a[1]));

    entries.slice(0, 6).forEach(([key, sim], i) => {
      const y = barsTop + i * rowH;
      const pct = Math.round((mode === "chaos" ? 1 - sim : sim) * 100);

      // row card
      roundRect(ctx, barInsetX, y, barW, 76, 38);
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.fill();
      ctx.lineWidth = 5;
      ctx.strokeStyle = "#0f172a";
      ctx.stroke();

      // label
      ctx.font = "900 32px 'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
      ctx.fillStyle = "#0f172a";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(TRAIT_LABELS[key].toUpperCase(), barInsetX + 36, y + 38);

      // bar track
      const trackX = barInsetX + 380;
      const trackY = y + 28;
      const trackW = barW - 380 - 130;
      const trackH = 20;
      roundRect(ctx, trackX, trackY, trackW, trackH, trackH / 2);
      ctx.fillStyle = "#e2e8f0";
      ctx.fill();
      // bar fill (mode-tinted)
      const barFillGrad = ctx.createLinearGradient(trackX, 0, trackX + trackW, 0);
      if (mode === "chaos") {
        barFillGrad.addColorStop(0, "#ffd84d");
        barFillGrad.addColorStop(1, "#ff6a3d");
      } else {
        barFillGrad.addColorStop(0, "#ff2d8a");
        barFillGrad.addColorStop(1, "#ff6a3d");
      }
      roundRect(ctx, trackX, trackY, Math.max(8, trackW * (pct / 100)), trackH, trackH / 2);
      ctx.fillStyle = barFillGrad;
      ctx.fill();

      // % readout
      ctx.font = "900 36px 'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
      ctx.fillStyle = "#0f172a";
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText(`${pct}%`, barInsetX + barW - 36, y + 38);
    });
  } else {
    // No traits → fallback row.
    ctx.font = "800 38px 'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
    ctx.fillStyle = "#0f172a";
    ctx.textAlign = "center";
    ctx.fillText(
      `${matchName ? `Me ✕ ${matchName}` : "Same face card vibes"}`,
      W / 2,
      barsTop + 80,
    );
  }

  // --- Tagline ---
  // The tagline doubles as the hook copy when the match is hidden. The full
  // reveal happens on the landing page we link to in the footer.
  const tagline = !matchOptIn
    ? "tap to see the reveal"
    : mode === "chaos"
      ? "find your chaos pick"
      : "find your doppel";
  drawStrokedText(ctx, tagline,
    W / 2, 1640, {
      font: "900 56px 'Bricolage Grotesque', 'Inter', system-ui, sans-serif",
      fill: "#0f172a",
      stroke: "#ffd84d",
      strokeWidth: 12,
    });

  // --- Footer pill ---
  ctx.save();
  ctx.translate(W / 2, 1780);
  const footW = 820, footH = 86;
  roundRect(ctx, -footW / 2, -footH / 2, footW, footH, footH / 2);
  ctx.fillStyle = "#0f172a";
  ctx.fill();
  ctx.font = "800 36px 'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText((url || "doppelcrush.app").replace(/^https?:\/\//, ""), 0, 2);
  ctx.restore();

  const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
  const dataUrl = canvas.toDataURL("image/png");
  return { blob, dataUrl };
}

export function downloadBlob(blob, filename = "doppelcrush-match.png") {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}
